import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { QUOTE_BY_ID_QUERY, CREATE_QUOTE_LINE_MUTATION, CREATE_QUOTE_LINE_ITEM_MUTATION, UPDATE_QUOTE_LINE_ITEM_MUTATION, UPDATE_QUOTE_LINE_ITEM_PRODUCT_TYPE_MUTATION, UPDATE_QUOTE_STATUS_MUTATION, UPDATE_QUOTE_MUTATION, ARCHIVE_QUOTE_LINE_MUTATION, ARCHIVE_QUOTE_LINE_ITEM_MUTATION, ARCHIVE_QUOTE_IMAGE_MUTATION } from '../graphql/quotes';
import { CREATE_ADDRESS_MUTATION } from '../graphql/customers';
import AddressPicker, { AddressOption, AddressPickerHandle } from '../components/Address/AddressPicker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SyncIcon from '@mui/icons-material/Sync';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CircularProgress from '@mui/material/CircularProgress';
import './QuoteDetail.css';
import CreateWorkOrder from './CreateWorkOrder';
import PhotoGallery, { GalleryPhoto } from '../components/PhotoGallery';
import QuickBooksEstimateDiffDialog from '../components/QuickBooksEstimateDiffDialog';
import { useQuickBooksSync, QbEstimateDiff } from '../hooks/useQuickBooksSync';

const SERVER_URL = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

interface QuoteLineItem {
  id: number;
  quantity: number;
  description: string;
  unitPrice: number;
  productType: string | null;
  notes: string | null;
  inventoryItemId: number | null;
  archivedAt: string | null;
}

// QuickBooks-facing product classification (issue #51) — independent of the
// quote_line.section a line happens to be grouped under. Values match
// schema/migrations/073_quote_line_item_product_type.sql's product_type_enum;
// GraphQL/PostGraphile represents them as SCREAMING_SNAKE_CASE.
const PRODUCT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'FABRIC', label: 'Fabric' },
  { value: 'VELCRO', label: 'Velcro' },
  { value: 'LINING', label: 'Lining' },
  { value: 'TRIM', label: 'Trim' },
  { value: 'ROLLEASE', label: 'Rollease' },
  { value: 'HARDWARE', label: 'Hardware' },
  { value: 'MOTORIZATION', label: 'Motorization' },
  { value: 'SHADE', label: 'Shade' },
  { value: 'INSTALLATION', label: 'Installation' },
  { value: 'MEASURE', label: 'Measure' },
  { value: 'OTHER', label: 'Other' },
  { value: 'B_SHADES', label: 'B Shades' },
  { value: 'CUSTOM', label: 'Custom' },
  { value: 'FURNITURE', label: 'Furniture' },
];

interface QuoteLine {
  id: number;
  lineNumber: number;
  section: string;
  archivedAt: string | null;
  quoteLineItemsByQuoteLineId: {
    nodes: QuoteLineItem[];
  };
}

interface QuoteImage {
  id: number;
  storedFilename: string;
  originalFilename: string;
  archivedAt: string | null;
}

interface DraftObjectForPhotos {
  id: number;
  name: string | null;
  notes: string | null;
  archivedAt: string | null;
  draftQuoteObjectImagesByDraftQuoteObjectId: { nodes: QuoteImage[] };
}

interface QuoteWorkOrder {
  id: number;
  description: string;
  travelerNumber: string | null;
  type: string | null;
  status: string;
  createdAt: string;
  dueAt: string | null;
  archivedAt: string | null;
  userByAssignedTo: {
    id: number;
    username: string;
  } | null;
}

interface QuoteData {
  id: number;
  name: string;
  status: string;
  createdAt: string;
  dueAt: string | null;
  followUpAt: string | null;
  notes: string | null;
  domainId: number;
  jobId: number;
  billToAddressId: number | null;
  shipToAddressId: number | null;
  createdBy: number;
  draftQuoteId: number | null;
  qbEstimateId: string | null;
  qbSyncedAt: string | null;
  qbEmailStatus: string | null;
  lineItemsUpdatedAt: string | null;
  jobByJobId: {
    id: number;
    name: string;
    addressByAddressId: AddressOption | null;
    customerByCustomerId: {
      id: number;
      name: string;
      email: string | null;
      phone: string | null;
      addressesByCustomerId: { nodes: AddressOption[] };
    };
  };
  addressByBillToAddressId: AddressOption | null;
  addressByShipToAddressId: AddressOption | null;
  draftQuoteByDraftQuoteId: {
    id: number;
    draftQuoteObjectsByDraftQuoteId: { nodes: DraftObjectForPhotos[] };
  } | null;
  quoteImagesByQuoteId: { nodes: QuoteImage[] };
  workOrdersByQuoteId: { nodes: QuoteWorkOrder[] };
  quoteLinesByQuoteId: {
    nodes: QuoteLine[];
  };
}

const statusColorMap: Record<string, { bg: string; text: string }> = {
  pending: { bg: '#f3f4f6', text: '#374151' },
  accepted: { bg: '#d1fae5', text: '#065f46' },
  rejected: { bg: '#fee2e2', text: '#dc2626' },
  closed: { bg: '#ede9fe', text: '#5b21b6' },
};

const woStatusColors: Record<string, { bg: string; text: string }> = {
  open: { bg: '#f3f4f6', text: '#374151' },
  in_progress: { bg: '#dbeafe', text: '#1e40af' },
  completed: { bg: '#d1fae5', text: '#065f46' },
  on_hold: { bg: '#fef3c7', text: '#92400e' },
};

const formatCents = (cents: number): string => {
  return (Number(cents) / 100).toFixed(2);
};

const ACCENT = '#9b0065';
const LIGHT_PINK = '#f5d0e8';

function PrintLayout({ quote, lines, quoteTotal }: { quote: QuoteData; lines: QuoteLine[]; quoteTotal: number }) {
  const billAddr = quote.addressByBillToAddressId;
  const shipAddr = quote.addressByShipToAddressId;

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '10pt', color: '#222', padding: '24pt 28pt', maxWidth: '7in', margin: '0 auto' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18pt' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: ACCENT, textTransform: 'uppercase', marginBottom: '4pt' }}>Bill To</div>
          <div style={{ fontWeight: 700 }}>{quote.jobByJobId.customerByCustomerId.name}</div>
          {billAddr && (
            <>
              <div>{billAddr.street}</div>
              <div>{billAddr.city}, {billAddr.state} {billAddr.zip}</div>
            </>
          )}
          {shipAddr && shipAddr.id !== billAddr?.id && (
            <div style={{ marginTop: '8pt' }}>
              <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: ACCENT, textTransform: 'uppercase', marginBottom: '4pt' }}>Ship To</div>
              <div>{shipAddr.street}</div>
              <div>{shipAddr.city}, {shipAddr.state} {shipAddr.zip}</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '2pt' }}>
          <div style={{ background: LIGHT_PINK, padding: '10pt 18pt', textAlign: 'center', minWidth: '72pt' }}>
            <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: ACCENT, textTransform: 'uppercase', marginBottom: '4pt' }}>Quote #</div>
            <div style={{ fontWeight: 700, color: ACCENT }}>{quote.id}</div>
          </div>
          <div style={{ background: LIGHT_PINK, padding: '10pt 18pt', textAlign: 'center', minWidth: '72pt' }}>
            <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: ACCENT, textTransform: 'uppercase', marginBottom: '4pt' }}>Date</div>
            <div style={{ fontWeight: 700, color: ACCENT }}>{new Date(quote.createdAt).toLocaleDateString()}</div>
          </div>
          <div style={{ background: ACCENT, padding: '10pt 18pt', textAlign: 'center', minWidth: '72pt' }}>
            <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: '#fff', textTransform: 'uppercase', marginBottom: '4pt' }}>Total</div>
            <div style={{ fontWeight: 700, color: '#fff', fontSize: '13pt' }}>${formatCents(quoteTotal)}</div>
          </div>
        </div>
      </div>

      {/* ── Client / Room row ── */}
      <div style={{ display: 'flex', gap: '32pt', borderTop: '1pt solid #ccc', paddingTop: '10pt', paddingBottom: '12pt' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: ACCENT, textTransform: 'uppercase', marginBottom: '2pt' }}>Client</div>
          <div>{quote.jobByJobId.customerByCustomerId.name}</div>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: ACCENT, textTransform: 'uppercase', marginBottom: '2pt' }}>Room</div>
          <div>{quote.name}</div>
        </div>
        {quote.notes && (
          <div>
            <div style={{ fontWeight: 700, fontSize: '7.5pt', letterSpacing: '0.12em', color: ACCENT, textTransform: 'uppercase', marginBottom: '2pt' }}>Notes</div>
            <div style={{ maxWidth: '200pt', fontSize: '9pt', color: '#555' }}>{quote.notes}</div>
          </div>
        )}
      </div>

      {/* ── Line items table ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
        <thead>
          <tr style={{ borderTop: '1.5pt solid #ccc', borderBottom: '1.5pt solid #ccc' }}>
            <th style={{ width: '8%', textAlign: 'right', paddingRight: '10pt', paddingTop: '5pt', paddingBottom: '5pt', fontWeight: 700, letterSpacing: '0.06em' }}>QTY</th>
            <th style={{ textAlign: 'left', paddingTop: '5pt', paddingBottom: '5pt', fontWeight: 700, letterSpacing: '0.06em' }}>ITEM</th>
            <th style={{ width: '13%', textAlign: 'right', paddingTop: '5pt', paddingBottom: '5pt', fontWeight: 700, letterSpacing: '0.06em' }}>RATE</th>
            <th style={{ width: '13%', textAlign: 'right', paddingTop: '5pt', paddingBottom: '5pt', fontWeight: 700, letterSpacing: '0.06em' }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const items = line.quoteLineItemsByQuoteLineId.nodes.filter((i) => !i.archivedAt);
            return (
              <React.Fragment key={line.id}>
                <tr>
                  <td colSpan={4} style={{ paddingTop: '9pt', paddingBottom: '2pt', fontWeight: 700, color: ACCENT, fontSize: '8.5pt', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {line.section}
                  </td>
                </tr>
                {items.map((item) => {
                  const qty = Number(item.quantity);
                  const rate = Number(item.unitPrice);
                  const amount = qty * rate;
                  return (
                    <tr key={item.id}>
                      <td style={{ textAlign: 'right', paddingRight: '10pt', paddingTop: '3pt', paddingBottom: '3pt', verticalAlign: 'top', color: '#444' }}>
                        {qty % 1 === 0 ? qty : qty.toFixed(2)}
                      </td>
                      <td style={{ paddingTop: '3pt', paddingBottom: '3pt', paddingRight: '12pt', lineHeight: 1.4 }}>{item.description}</td>
                      <td style={{ textAlign: 'right', paddingTop: '3pt', paddingBottom: '3pt', color: '#444' }}>{formatCents(rate)}</td>
                      <td style={{ textAlign: 'right', paddingTop: '3pt', paddingBottom: '3pt' }}>{formatCents(amount)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* ── Footer ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1pt solid #ccc', paddingTop: '10pt', marginTop: '10pt' }}>
        <div style={{ minWidth: '180pt' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3pt' }}>
            <span style={{ fontWeight: 700, color: ACCENT, fontSize: '8.5pt', letterSpacing: '0.08em' }}>SUBTOTAL</span>
            <span>${formatCents(quoteTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6pt', borderTop: '1pt solid #ccc', paddingTop: '6pt' }}>
            <span style={{ fontWeight: 700, color: ACCENT, fontSize: '11pt', letterSpacing: '0.08em' }}>TOTAL</span>
            <span style={{ fontWeight: 700, color: ACCENT, fontSize: '14pt' }}>${formatCents(quoteTotal)}</span>
          </div>
          <div style={{ textAlign: 'right', marginTop: '6pt', fontWeight: 700, color: ACCENT, fontSize: '9pt', letterSpacing: '0.06em' }}>THANK YOU.</div>
        </div>
      </div>
    </div>
  );
}

export default function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [showAddLineForm, setShowAddLineForm] = useState(false);
  const [newLineSection, setNewLineSection] = useState<'fabric' | 'labor' | 'hardware' | 'furniture' | 'other' | 'shade' | 'installation' | 'measure'>('fabric');
  const [addingItemToLineId, setAddingItemToLineId] = useState<number | null>(null);
  const [newItemData, setNewItemData] = useState({
    description: '',
    quantity: '',
    unitPrice: '',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [showWorkOrderDialog, setShowWorkOrderDialog] = useState(false);
  const [showQbDiffDialog, setShowQbDiffDialog] = useState(false);
  const [autoQbDiff, setAutoQbDiff] = useState<QbEstimateDiff | undefined>(undefined);
  // Bumped by any mutation that touches this quote's line items (add/edit/
  // archive/product-type change) so a d2x-side edit counts as a new state
  // worth re-checking, the same way reopening the page after a QBO-side edit
  // does — see the autoCheckedQuoteRef effect below.
  const [localEditVersion, setLocalEditVersion] = useState(0);
  const autoCheckedKeyRef = useRef<string | null>(null);
  const autoCheckedStatusRef = useRef<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState({ description: '', quantity: '', unitPrice: '' });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [qbMessage, setQbMessage] = useState<string | null>(null);
  const [qbMessageIsError, setQbMessageIsError] = useState(false);
  const [newWorkOrderId, setNewWorkOrderId] = useState<number | null>(null);
  const { sendToQuickBooks, checkQuickBooksStatus, fetchEstimateDiff, isPending: isQbPending } = useQuickBooksSync();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ billToAddressId: '' as number | '', shipToAddressId: '' as number | '', notes: '' });
  const [savingDetails, setSavingDetails] = useState(false);
  const billToPickerRef = useRef<AddressPickerHandle>(null);
  const shipToPickerRef = useRef<AddressPickerHandle>(null);

  const { data, loading, refetch } = useQuery(QUOTE_BY_ID_QUERY, {
    variables: { id: parseInt(id!) },
    skip: !id,
  });

  const [createQuoteLine, { loading: creatingLine }] = useMutation(CREATE_QUOTE_LINE_MUTATION);
  const [createQuoteLineItem, { loading: creatingItem }] = useMutation(CREATE_QUOTE_LINE_ITEM_MUTATION);
  const [updateQuoteLineItem] = useMutation(UPDATE_QUOTE_LINE_ITEM_MUTATION);
  const [updateQuoteLineItemProductType] = useMutation(UPDATE_QUOTE_LINE_ITEM_PRODUCT_TYPE_MUTATION);
  const [updateQuoteStatus, { loading: updatingStatus }] = useMutation(UPDATE_QUOTE_STATUS_MUTATION);
  const [updateQuote] = useMutation(UPDATE_QUOTE_MUTATION);
  const [createAddress] = useMutation(CREATE_ADDRESS_MUTATION);
  const [archiveQuoteLine] = useMutation(ARCHIVE_QUOTE_LINE_MUTATION);
  const [archiveQuoteLineItem] = useMutation(ARCHIVE_QUOTE_LINE_ITEM_MUTATION);
  const [archiveQuoteImage] = useMutation(ARCHIVE_QUOTE_IMAGE_MUTATION);

  const quote: QuoteData | undefined = data?.quoteById;
  const allLines = quote?.quoteLinesByQuoteId?.nodes || [];
  const lines = allLines.filter(l => !l.archivedAt);
  const workOrders = (quote?.workOrdersByQuoteId?.nodes || []).filter(wo => !wo.archivedAt);

  // On opening a quote that's already synced to QuickBooks, silently check
  // for drift once per (quote, edit-state) pair — only surfaces the "Pull
  // from QB" dialog if there's actually something to review, so nothing
  // changes for the common case where QuickBooks and this quote already
  // agree. Keyed off a ref (not just quote.id in the effect deps) so a
  // refetch that didn't change anything relevant — e.g. after applying a
  // diff — doesn't re-trigger the check and pop the dialog back open.
  //
  // The key includes two independent edit signals, because either can
  // change the local side without the other: localEditVersion for edits
  // made from this page itself (add/edit/archive a line item here), and
  // quote.lineItemsUpdatedAt (migration 090) for edits applied from
  // elsewhere — e.g. DraftQuoteDetail's "Apply Changes to Draft Object" /
  // resyncFinalizedQuote, which rewrites this quote's line items without
  // ever touching this page's own state. Without the latter, an
  // already-mounted QuoteDetail instance would keep believing it had
  // already checked and silently suppress a real diff created by that
  // other page.
  useEffect(() => {
    if (!quote?.id || !quote.qbEstimateId) return;
    const key = `${quote.id}:${localEditVersion}:${quote.lineItemsUpdatedAt ?? ''}`;
    if (autoCheckedKeyRef.current === key) return;
    autoCheckedKeyRef.current = key;

    let cancelled = false;
    (async () => {
      const result = await fetchEstimateDiff(quote.id);
      if (cancelled || !result.ok || !result.diff) return;
      const hasChanges =
        result.diff.removed.length > 0 || result.diff.changed.length > 0 || result.diff.added.length > 0;
      if (hasChanges) {
        setAutoQbDiff(result.diff);
        setShowQbDiffDialog(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id, quote?.qbEstimateId, localEditVersion, quote?.lineItemsUpdatedAt]);

  // Same idea as the diff auto-check above, for the Accepted/Rejected/Closed
  // TxnStatus pull — fully silent (no qbMessage banner, no dialog to open in
  // the first place): just refetches so quote.status reflects QuickBooks
  // without requiring a manual "Check QB Status" click every time.
  useEffect(() => {
    if (!quote?.id || !quote.qbEstimateId) return;
    if (autoCheckedStatusRef.current === quote.id) return;
    autoCheckedStatusRef.current = quote.id;

    let cancelled = false;
    (async () => {
      const result = await checkQuickBooksStatus(quote.id);
      if (cancelled || !result.ok) return;
      await refetch();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id, quote?.qbEstimateId]);

  const handleAddLine = async () => {
    if (!quote?.id) return;

    setError(null);
    const nextLineNumber = allLines.length > 0 ? Math.max(...allLines.map((l) => l.lineNumber)) + 1 : 1;

    try {
      await createQuoteLine({
        variables: {
          quoteId: quote.id,
          lineNumber: nextLineNumber,
          section: newLineSection,
        },
      });
      await refetch();
      setShowAddLineForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add line');
    }
  };

  const handleAddLineItem = async (lineId: number) => {
    if (!newItemData.description.trim() || !newItemData.quantity || !newItemData.unitPrice) {
      setError('Please fill in all required fields: description, quantity, and unit price');
      return;
    }

    setError(null);
    const quantity = parseFloat(newItemData.quantity);
    const unitPriceInCents = Math.round(parseFloat(newItemData.unitPrice) * 100);

    if (quantity <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }

    if (unitPriceInCents < 0) {
      setError('Unit price cannot be negative');
      return;
    }

    try {
      await createQuoteLineItem({
        variables: {
          quoteLineId: lineId,
          quantity,
          description: newItemData.description,
          unitPrice: unitPriceInCents,
          notes: newItemData.notes || null,
        },
      });
      await refetch();
      setLocalEditVersion((v) => v + 1);
      setNewItemData({ description: '', quantity: '', unitPrice: '', notes: '' });
      setAddingItemToLineId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to add line item');
    }
  };

  const startEditItem = (item: QuoteLineItem) => {
    setEditingItemId(item.id);
    setEditValues({
      description: item.description,
      quantity: String(Number(item.quantity)),
      unitPrice: String(Number(item.unitPrice) / 100),
    });
  };

  const handleSaveItem = async (itemId: number) => {
    setError(null);
    try {
      await updateQuoteLineItem({
        variables: {
          id: itemId,
          description: editValues.description,
          quantity: parseFloat(editValues.quantity),
          unitPrice: Math.round(parseFloat(editValues.unitPrice) * 100),
        },
      });
      await refetch();
      setLocalEditVersion((v) => v + 1);
      setEditingItemId(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update item');
    }
  };

  const handleProductTypeChange = async (itemId: number, productType: string) => {
    setError(null);
    try {
      await updateQuoteLineItemProductType({
        variables: { id: itemId, productType: productType || null },
      });
      await refetch();
      setLocalEditVersion((v) => v + 1);
    } catch (err: any) {
      setError(err.message || 'Failed to update product type');
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!quote?.id) return;
    setQbMessage(null);
    try {
      await updateQuoteStatus({ variables: { id: quote.id, status: newStatus } });
      await refetch();
    } catch (err: any) {
      setQbMessageIsError(true);
      setQbMessage(err.message || 'Failed to update quote status');
    }
  };

  const handleArchiveLine = async (lineId: number) => {
    await archiveQuoteLine({ variables: { id: lineId, archivedAt: new Date().toISOString() } });
    await refetch();
    setLocalEditVersion((v) => v + 1);
  };

  const handleArchiveItem = async (itemId: number) => {
    await archiveQuoteLineItem({ variables: { id: itemId, archivedAt: new Date().toISOString() } });
    await refetch();
    setLocalEditVersion((v) => v + 1);
  };

  const handleUploadQuotePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !quote?.id) return;

    setError(null);
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('quoteId', String(quote.id));
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/upload/quote-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || 'Upload failed');
      }
      await refetch();
    } catch (err: any) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSendToQuickBooks = async () => {
    if (!quote?.id) return;
    setQbMessage(null);
    const result = await sendToQuickBooks(quote.id);
    setQbMessageIsError(!result.ok);
    setQbMessage(result.message);
    if (result.ok) await refetch();
  };

  const handleCheckQuickBooksStatus = async () => {
    if (!quote?.id) return;
    setQbMessage(null);
    const result = await checkQuickBooksStatus(quote.id);
    setQbMessageIsError(!result.ok);
    setQbMessage(result.message);
    await refetch();
  };

  const handleArchiveQuotePhoto = async (imageId: number) => {
    await archiveQuoteImage({ variables: { id: imageId, archivedAt: new Date().toISOString() } });
    await refetch();
  };

  if (loading) {
    return <div className="quote-detail-page">Loading...</div>;
  }

  if (!quote) {
    return (
      <div className="quote-detail-page">
        <button onClick={() => navigate('/quotes')} className="back-button btn-icon">
          <ArrowBackIcon fontSize="small" /> Back to Quotes
        </button>
        <div className="error-message">Quote not found</div>
      </div>
    );
  }

  const addressText = (addr: AddressOption | null) =>
    addr ? `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}` : '—';

  const billToAddresses = quote.jobByJobId.customerByCustomerId.addressesByCustomerId.nodes;
  const shipToAddresses = (() => {
    const seen = new Set<number>();
    const merged = [
      ...(quote.jobByJobId.addressByAddressId ? [quote.jobByJobId.addressByAddressId] : []),
      ...billToAddresses,
    ];
    return merged.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
  })();

  const openEditDetails = () => {
    setDetailsForm({
      billToAddressId: quote.addressByBillToAddressId?.id ?? '',
      shipToAddressId: quote.addressByShipToAddressId?.id ?? '',
      notes: quote.notes || '',
    });
    setEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    setError(null);
    try {
      const billToAddressId = await billToPickerRef.current?.commit() ?? detailsForm.billToAddressId;
      const shipToAddressId = await shipToPickerRef.current?.commit() ?? detailsForm.shipToAddressId;
      await updateQuote({
        variables: {
          id: quote.id,
          billToAddressId: billToAddressId || null,
          shipToAddressId: shipToAddressId || null,
          notes: detailsForm.notes || null,
        },
      });
      setEditingDetails(false);
      await refetch();
    } catch (err: any) {
      setError(err.message || 'Failed to save quote details');
    } finally {
      setSavingDetails(false);
    }
  };

  const createAddressForCustomer = async (fields: { street: string; city: string; state: string; zip: string }) => {
    const result = await createAddress({
      variables: {
        customerId: quote.jobByJobId.customerByCustomerId.id,
        domainId: user!.domain_id,
        street: fields.street,
        city: fields.city,
        state: fields.state,
        zip: fields.zip,
        addressType: 'primary',
      },
    });
    return result.data.createAddress.address;
  };

  // PostGraphile only uppercases enum values that contain symbols it needs to
  // sanitize; plain lowercase Postgres enum labels like "sent" pass through
  // to GraphQL unchanged. quote.status is already lowercase — this call is a
  // defensive no-op, kept in case that ever changes.
  const statusKey = quote.status.toLowerCase();
  const statusColors = statusColorMap[statusKey] || statusColorMap.pending;

  const quoteTotal = lines.reduce((sum, line) => {
    const lineSubtotal = line.quoteLineItemsByQuoteLineId.nodes.filter((i) => !i.archivedAt).reduce((itemSum, item) => {
      return itemSum + Number(item.quantity) * Number(item.unitPrice);
    }, 0);
    return sum + lineSubtotal;
  }, 0);

  return (
    <div className="quote-detail-page">
      <button onClick={() => navigate('/quotes')} className="back-button no-print btn-icon">
        <ArrowBackIcon fontSize="small" /> Back to Quotes
      </button>

      {/* Invoice layout — only visible when printing */}
      <div className="print-only">
        <PrintLayout quote={quote} lines={lines} quoteTotal={quoteTotal} />
      </div>

      <div className="detail-container no-print">
        {/* Quote Header */}
        <div className="detail-header">
          <h1>{quote.name} <span style={{ color: '#6b7280', fontWeight: 500, fontSize: '0.6em' }}>#{quote.id}</span></h1>
          <select
            className="status-badge"
            value={quote.status}
            disabled={updatingStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            style={{
              backgroundColor: statusColors.bg,
              color: statusColors.text,
              border: 'none',
              appearance: 'none',
              WebkitAppearance: 'none',
              cursor: updatingStatus ? 'default' : 'pointer',
            }}
          >
            {Object.keys(statusColorMap).map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <div className="detail-header-actions">
            {quote.qbEstimateId ? (
              <>
                <span
                  className="btn-icon"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#065f46', background: '#d1fae5', borderRadius: 4, padding: '4px 10px' }}
                  title={quote.qbSyncedAt ? `Synced ${new Date(quote.qbSyncedAt).toLocaleString()}` : undefined}
                >
                  <CloudDoneIcon fontSize="small" /> QuickBooks #{quote.qbEstimateId}
                </span>
                {quote.qbEmailStatus === 'EmailSent' && (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13, color: '#6b7280', padding: '4px 0' }}
                    title="This estimate has been emailed to the customer via QuickBooks"
                  >
                    Emailed
                  </span>
                )}
                <button
                  className="add-button btn-icon"
                  style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                  onClick={handleCheckQuickBooksStatus}
                  disabled={isQbPending(quote.id)}
                  title="Pull the latest accepted/rejected status from QuickBooks"
                >
                  {isQbPending(quote.id) ? (
                    <CircularProgress size={14} thickness={5} sx={{ color: '#374151' }} />
                  ) : (
                    <SyncIcon fontSize="small" />
                  )}
                  {isQbPending(quote.id) ? 'Checking…' : 'Check QB Status'}
                </button>
                <button
                  className="add-button btn-icon"
                  style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                  onClick={() => { setAutoQbDiff(undefined); setShowQbDiffDialog(true); }}
                  disabled={isQbPending(quote.id)}
                  title="Pull the current estimate from QuickBooks and compare it to this quote"
                >
                  <CompareArrowsIcon fontSize="small" /> Pull from QB
                </button>
              </>
            ) : statusKey !== 'rejected' ? (
              <button
                className="add-button btn-icon"
                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                onClick={handleSendToQuickBooks}
                disabled={isQbPending(quote.id)}
              >
                {isQbPending(quote.id) ? (
                  <CircularProgress size={14} thickness={5} sx={{ color: '#374151' }} />
                ) : (
                  <CloudUploadIcon fontSize="small" />
                )}
                {isQbPending(quote.id) ? 'Sending…' : 'Send to QuickBooks'}
              </button>
            ) : null}
            {quote.draftQuoteId && (
              <button
                className="add-button btn-icon"
                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                onClick={() => navigate(`/draft-quotes/${quote.draftQuoteId}`)}
                title="Edit this quote's draft — use Update Finalized Quote there to push changes back here"
              >
                <EditIcon fontSize="small" /> Edit Draft Quote
              </button>
            )}
            <button
              className="add-button btn-icon"
              style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
              onClick={() => window.print()}
            >
              <PictureAsPdfIcon fontSize="small" /> PDF
            </button>
            <button
              className="add-button btn-icon"
              onClick={() => setShowWorkOrderDialog(true)}
            >
              <AddIcon fontSize="small" /> Work order
            </button>
          </div>
        </div>

        {qbMessage && (
          <div
            style={{
              margin: '8px 0 12px',
              padding: '8px 12px',
              borderRadius: 4,
              fontSize: 13,
              background: qbMessageIsError ? '#fee2e2' : '#d1fae5',
              color: qbMessageIsError ? '#dc2626' : '#065f46',
            }}
          >
            {qbMessage}
          </div>
        )}

        {showWorkOrderDialog && (
          <CreateWorkOrder
            quoteId={quote.id}
            onClose={() => setShowWorkOrderDialog(false)}
            onComplete={async (id) => {
              setShowWorkOrderDialog(false);
              setNewWorkOrderId(id);
              await refetch();
            }}
          />
        )}

        {showQbDiffDialog && (
          <QuickBooksEstimateDiffDialog
            quoteId={quote.id}
            draftQuoteId={quote.draftQuoteId}
            initialDiff={autoQbDiff}
            onClose={() => { setShowQbDiffDialog(false); setAutoQbDiff(undefined); }}
            onApplied={refetch}
          />
        )}

        <div className="quote-dashboard-grid">
        <div className="quote-dashboard-left">

        {/* Quote Details Section */}
        <div className="info-block">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Quote Details</h3>
            {!editingDetails && (
              <button
                className="add-button btn-icon"
                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                onClick={openEditDetails}
              >
                <EditIcon fontSize="small" /> Edit
              </button>
            )}
          </div>
          <div className="info-grid">
            <div className="info-row">
              <label>Customer</label>
              <div>{quote.jobByJobId.customerByCustomerId.name}</div>
            </div>
            {editingDetails ? (
              <>
                <div className="info-row">
                  <label>Bill To</label>
                  <div style={{ maxWidth: 400 }}>
                    <AddressPicker
                      ref={billToPickerRef}
                      label=""
                      addresses={billToAddresses}
                      value={detailsForm.billToAddressId}
                      onChange={(v) => setDetailsForm({ ...detailsForm, billToAddressId: v })}
                      disabled={savingDetails}
                      onCreateAddress={createAddressForCustomer}
                    />
                  </div>
                </div>
                <div className="info-row">
                  <label>Ship To</label>
                  <div style={{ maxWidth: 400 }}>
                    <AddressPicker
                      ref={shipToPickerRef}
                      label=""
                      addresses={shipToAddresses}
                      value={detailsForm.shipToAddressId}
                      onChange={(v) => setDetailsForm({ ...detailsForm, shipToAddressId: v })}
                      disabled={savingDetails}
                      onCreateAddress={createAddressForCustomer}
                    />
                  </div>
                </div>
                <div className="info-row">
                  <label>Notes</label>
                  <textarea
                    style={{ width: '100%', minHeight: 72, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    value={detailsForm.notes}
                    disabled={savingDetails}
                    onChange={(e) => setDetailsForm({ ...detailsForm, notes: e.target.value })}
                  />
                </div>
                <div className="info-row">
                  <label></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="save-button" disabled={savingDetails} onClick={handleSaveDetails}>
                      {savingDetails ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      style={{ padding: '10px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
                      disabled={savingDetails}
                      onClick={() => setEditingDetails(false)}
                    >
                      <CloseIcon fontSize="small" /> Cancel
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="info-row">
                  <label>Bill To</label>
                  <div>{addressText(quote.addressByBillToAddressId)}</div>
                </div>
                <div className="info-row">
                  <label>Ship To</label>
                  <div>{addressText(quote.addressByShipToAddressId)}</div>
                </div>
              </>
            )}
            <div className="info-block-mini-grid">
              <div className="info-row">
                <label>Status</label>
                <div style={{ textTransform: 'capitalize' }}>{statusKey}</div>
              </div>
              <div className="info-row">
                <label>Created</label>
                <div>{new Date(quote.createdAt).toLocaleDateString()}</div>
              </div>
            </div>
            {quote.dueAt && (
              <div className="info-row">
                <label>Due Date</label>
                <div>{new Date(quote.dueAt).toLocaleDateString()}</div>
              </div>
            )}
            {!editingDetails && quote.notes && (
              <div className="info-row">
                <label>Notes</label>
                <div style={{ whiteSpace: 'pre-wrap' }}>{quote.notes}</div>
              </div>
            )}
            {quote.draftQuoteId && (
              <div className="info-row">
                <label>Draft Quote</label>
                <div>
                  <a href={`/draft-quotes/${quote.draftQuoteId}`} className="btn-icon" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>
                    View source draft <ArrowForwardIcon fontSize="small" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes Section — free-text shop notes collected per draft quote
            object (e.g. "with tailored front pleats and top to remain
            pleated even when down"), read directly off the source draft
            quote objects rather than injected as quote_line_item rows —
            see finalizeQuotePlugin.ts's comment on why. */}
        {(() => {
          const notedObjects = (quote.draftQuoteByDraftQuoteId?.draftQuoteObjectsByDraftQuoteId.nodes || [])
            .filter((o) => !o.archivedAt && o.notes && o.notes.trim());
          if (notedObjects.length === 0) return null;
          return (
            <div className="info-block no-print">
              <h3>Notes</h3>
              {notedObjects.map((obj) => (
                <div key={obj.id} style={{ marginBottom: 8, fontSize: 14 }}>
                  <span style={{ fontWeight: 600 }}>{obj.name || 'Object'}:</span>{' '}
                  <span style={{ color: '#374151' }}>{obj.notes}</span>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Photos Section */}
        <div className="info-block no-print">
          <h3>Photos</h3>

          {(() => {
            const draftObjects = (quote.draftQuoteByDraftQuoteId?.draftQuoteObjectsByDraftQuoteId.nodes || [])
              .filter((o) => !o.archivedAt);
            const draftPhotoGroups = draftObjects
              .map((obj) => ({
                obj,
                photos: obj.draftQuoteObjectImagesByDraftQuoteObjectId.nodes
                  .filter((img) => !img.archivedAt)
                  .map((img): GalleryPhoto => ({
                    key: `draft-${img.id}`,
                    url: `${SERVER_URL}/uploads/object-images/${img.storedFilename}`,
                    filename: img.originalFilename,
                  })),
              }))
              .filter((g) => g.photos.length > 0);

            const quotePhotos: GalleryPhoto[] = (quote.quoteImagesByQuoteId?.nodes || [])
              .filter((img) => !img.archivedAt)
              .map((img) => ({
                key: String(img.id),
                url: `${SERVER_URL}/uploads/quote-images/${img.storedFilename}`,
                filename: img.originalFilename,
              }));

            return (
              <>
                {draftPhotoGroups.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    {draftPhotoGroups.map(({ obj, photos }) => (
                      <div key={obj.id} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>
                          {obj.name || 'Untitled Object'}
                        </div>
                        <PhotoGallery photos={photos} size={60} />
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>
                  Quote Photos
                </div>
                <PhotoGallery
                  photos={quotePhotos}
                  onRemove={(key) => handleArchiveQuotePhoto(parseInt(key, 10))}
                  emptyText="No quote photos yet."
                  size={60}
                />
                <div style={{ marginTop: 10 }}>
                  {uploadingPhoto ? (
                    <span style={{ fontSize: 13, color: '#6b7280' }}>Uploading...</span>
                  ) : (
                    <button className="add-item-button btn-icon" style={{ fontSize: 12 }} onClick={() => fileInputRef.current?.click()}>
                      <AddPhotoAlternateIcon sx={{ fontSize: 15 }} /> Add Photo
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleUploadQuotePhoto}
                  />
                </div>
              </>
            );
          })()}
        </div>

        {/* Work Orders Section */}
        <div className="info-block no-print">
          <h3>Work Orders ({workOrders.length})</h3>
          {workOrders.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>
              No work orders yet. Click "Work order" to create a traveler sheet from this quote.
            </div>
          ) : (
            <div className="wo-compact-list">
              {workOrders.map((wo) => {
                const colors = woStatusColors[wo.status] || woStatusColors.open;
                const isNew = wo.id === newWorkOrderId;
                return (
                  <div
                    key={wo.id}
                    className="wo-compact-row"
                    onClick={() => navigate(`/work-orders/${wo.id}`)}
                    style={{ backgroundColor: isNew ? '#eef2ff' : undefined }}
                  >
                    <span className="wo-num">{wo.travelerNumber ? `#${wo.travelerNumber}` : `#${wo.id}`}</span>
                    <a
                      href={`/work-orders/${wo.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="wo-desc"
                    >
                      {wo.description}
                    </a>
                    {isNew && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', flexShrink: 0 }}>NEW</span>
                    )}
                    <span
                      className="section-badge"
                      style={{ backgroundColor: colors.bg, color: colors.text, fontSize: 10, marginLeft: 'auto', flexShrink: 0 }}
                    >
                      {wo.status.replace('_', ' ')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        </div>
        <div className="quote-dashboard-right">

        {/* Quote Lines Section */}
        <div>
          <div className="quote-lines-header">
            <h2>Quote Lines ({lines.length})</h2>
            <button className="add-button no-print btn-icon" onClick={() => setShowAddLineForm(!showAddLineForm)}>
              {showAddLineForm ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              {showAddLineForm ? 'Close' : 'Add Line'}
            </button>
          </div>

          {/* Add Line Form */}
          {showAddLineForm && (
            <div className="add-line-form">
              <div className="form-group">
                <label>Section</label>
                <select
                  value={newLineSection}
                  onChange={(e) => setNewLineSection(e.target.value as any)}
                  className="input"
                  disabled={creatingLine}
                >
                  <option value="fabric">Fabric</option>
                  <option value="labor">Labor</option>
                  <option value="hardware">Hardware</option>
                  <option value="furniture">Furniture</option>
                  <option value="shade">Shade</option>
                  <option value="installation">Installation</option>
                  <option value="measure">Measure</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <button onClick={handleAddLine} className="save-button" disabled={creatingLine}>
                {creatingLine ? 'Adding...' : 'Add Line'}
              </button>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {/* Existing Lines */}
          {lines.length === 0 ? (
            <div className="empty-state">No quote lines yet. Click "Add Line" to get started.</div>
          ) : (
            <div className="quote-lines-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style={{ width: 70 }}>Qty</th>
                    <th style={{ width: 90 }}>Unit</th>
                    <th style={{ width: 90 }}>Total</th>
                    <th style={{ width: 120 }}>Type</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const activeItems = line.quoteLineItemsByQuoteLineId.nodes.filter(i => !i.archivedAt);
                    const lineSubtotal = activeItems.reduce((sum, item) => {
                      return sum + Number(item.quantity) * Number(item.unitPrice);
                    }, 0);

                    return (
                      <React.Fragment key={line.id}>
                        <tr className="section-group-row">
                          <td colSpan={6}>
                            <span className={`section-badge section-badge--${line.section}`}>{line.section}</span>
                            <span style={{ marginLeft: 8, fontSize: 12, color: '#9ca3af' }}>Line {line.lineNumber}</span>
                            <button
                              onClick={() => handleArchiveLine(line.id)}
                              style={{ float: 'right', marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                              title="Archive this line"
                            >
                              <ArchiveIcon sx={{ fontSize: 14 }} />
                            </button>
                            <button
                              onClick={() => setAddingItemToLineId(addingItemToLineId === line.id ? null : line.id)}
                              disabled={creatingItem}
                              style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}
                              title="Add item"
                            >
                              {addingItemToLineId === line.id ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                            </button>
                          </td>
                        </tr>

                        {/* Add Item Form */}
                        {addingItemToLineId === line.id && (
                          <tr>
                            <td colSpan={6}>
                              <div className="add-item-form">
                                <div className="form-group">
                                  <label>
                                    Description <span style={{ color: '#dc2626' }}>*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="input"
                                    placeholder="e.g., Roman shade with blackout lining"
                                    value={newItemData.description}
                                    onChange={(e) => setNewItemData({ ...newItemData, description: e.target.value })}
                                    disabled={creatingItem}
                                  />
                                </div>
                                <div className="add-item-form-row">
                                  <div className="form-group" style={{ width: 120 }}>
                                    <label>
                                      Qty <span style={{ color: '#dc2626' }}>*</span>
                                    </label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0.01"
                                      className="input"
                                      value={newItemData.quantity}
                                      onChange={(e) => setNewItemData({ ...newItemData, quantity: e.target.value })}
                                      disabled={creatingItem}
                                    />
                                  </div>
                                  <div className="form-group" style={{ width: 160 }}>
                                    <label>
                                      Unit Price ($) <span style={{ color: '#dc2626' }}>*</span>
                                    </label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      className="input"
                                      placeholder="0.00"
                                      value={newItemData.unitPrice}
                                      onChange={(e) => setNewItemData({ ...newItemData, unitPrice: e.target.value })}
                                      disabled={creatingItem}
                                    />
                                  </div>
                                </div>
                                <div className="form-group">
                                  <label>Notes</label>
                                  <input
                                    type="text"
                                    className="input"
                                    placeholder="Optional notes about this item"
                                    value={newItemData.notes}
                                    onChange={(e) => setNewItemData({ ...newItemData, notes: e.target.value })}
                                    disabled={creatingItem}
                                  />
                                </div>
                                <button
                                  onClick={() => handleAddLineItem(line.id)}
                                  className="save-button"
                                  disabled={creatingItem}
                                >
                                  {creatingItem ? 'Adding...' : 'Add Item'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}

                        {activeItems.map((item) => {
                          const itemTotal = Number(item.quantity) * Number(item.unitPrice);
                          const isEditing = editingItemId === item.id;
                          return isEditing ? (
                            <tr key={item.id} style={{ backgroundColor: '#fffbeb' }}>
                              <td>
                                <input
                                  className="input"
                                  style={{ padding: '4px 8px', fontSize: 13, width: '100%' }}
                                  value={editValues.description}
                                  onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  className="input"
                                  type="number" step="0.01" min="0"
                                  style={{ padding: '4px 8px', fontSize: 13, width: 80 }}
                                  value={editValues.quantity}
                                  onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  className="input"
                                  type="number" step="0.01" min="0"
                                  style={{ padding: '4px 8px', fontSize: 13, width: 90 }}
                                  value={editValues.unitPrice}
                                  onChange={(e) => setEditValues({ ...editValues, unitPrice: e.target.value })}
                                />
                              </td>
                              <td style={{ color: '#9ca3af' }}>—</td>
                              <td>
                                <select
                                  className="input"
                                  style={{ padding: '4px 6px', fontSize: 12, width: '100%' }}
                                  value={item.productType || ''}
                                  onChange={(e) => handleProductTypeChange(item.id, e.target.value)}
                                >
                                  <option value="">—</option>
                                  {PRODUCT_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button onClick={() => handleSaveItem(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', marginRight: 4 }} title="Save"><CheckIcon fontSize="small" /></button>
                                <button onClick={() => setEditingItemId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }} title="Cancel"><CloseIcon fontSize="small" /></button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={item.id}>
                              <td>{item.description}</td>
                              <td>{Number(item.quantity).toFixed(2)}</td>
                              <td>${formatCents(item.unitPrice)}</td>
                              <td>${formatCents(itemTotal)}</td>
                              <td>
                                <select
                                  className="input"
                                  style={{ padding: '4px 6px', fontSize: 12, width: '100%' }}
                                  value={item.productType || ''}
                                  onChange={(e) => handleProductTypeChange(item.id, e.target.value)}
                                >
                                  <option value="">—</option>
                                  {PRODUCT_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button onClick={() => startEditItem(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', marginRight: 2 }} title="Edit"><EditIcon sx={{ fontSize: 16 }} /></button>
                                <button onClick={() => handleArchiveItem(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }} title="Archive"><BlockIcon sx={{ fontSize: 16 }} /></button>
                              </td>
                            </tr>
                          );
                        })}

                        <tr className="section-subtotal-row">
                          <td colSpan={6}>Subtotal: ${formatCents(lineSubtotal)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Quote Total */}
          {lines.length > 0 && (
            <div className="quote-total">
              <strong>Quote Total: ${formatCents(quoteTotal)}</strong>
            </div>
          )}
        </div>

        </div>
        </div>
      </div>
    </div>
  );
}
